import React, { useState, useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "../ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { Badge } from "../ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  Building,
  Users,
  Settings,
  UserPlus,
  MoreVertical,
  Trash2,
  Edit,
  Crown,
  User,
  Mail,
  X,
  Plus,
} from "lucide-react";
import { toast } from "../../hooks/use-toast";
import {
  getOrganization,
  getOrganizationMembers,
  updateOrganization,
  deleteOrganization,
  removeOrganizationMember,
  inviteToOrganization,
} from "../../services/organizationService";
import { getOrganizationGroups } from "../../services/teamService";
import { InviteDialog } from "../invitation/InviteDialog";
import { CreateTeamDialog } from "../team/CreateTeamDialog";
import {
  Organization,
  OrganizationMember,
  MemberRole,
} from "../../types/organization";
import { Team } from "../../services/teamService";
import { supabase } from "../../utils/supabaseClient";
import { useUserPermissions } from "../../hooks/useUserPermissions";

const editFormSchema = z.object({
  name: z.string().min(2, "Organization name must be at least 2 characters."),
  description: z.string().optional(),
});

type EditFormValues = z.infer<typeof editFormSchema>;

interface OrganizationManageDialogProps {
  organization: Organization;
  onSuccess?: () => void;
  onDelete?: () => void;
  trigger?: React.ReactNode;
}

export function OrganizationManageDialog({
  organization,
  onSuccess,
  onDelete,
  trigger,
}: OrganizationManageDialogProps) {
  const { permissions } = useUserPermissions();
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [memberToRemove, setMemberToRemove] =
    useState<OrganizationMember | null>(null);

  // Invite functionality state
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [pendingInvites, setPendingInvites] = useState<string[]>([]);
  const [inviteErrors, setInviteErrors] = useState<{ [key: string]: string }>({});
  const [sendingInvites, setSendingInvites] = useState(false);

  // Determine workspace type
  const isPersonalWorkspace = organization.tenant_type === 'personal';
  const inviteButtonText = isPersonalWorkspace ? 'Invite Users' : 'Invite Members';
  const inviteDialogTitle = isPersonalWorkspace
    ? `Invite Users to ${organization.name}`
    : `Invite Members to ${organization.name}`;

  const editForm = useForm<EditFormValues>({
    resolver: zodResolver(editFormSchema),
    defaultValues: {
      name: organization.name,
      description: organization.description || "",
    },
  });

  const loadData = async () => {
    try {
      setLoading(true);
      console.log(`[OrganizationManageDialog] 🔍 Loading data for organization: ${organization.id}`);

      const [membersData, teamsData] = await Promise.all([
        getOrganizationMembers(organization.id),
        getOrganizationGroups(organization.id),
      ]);

      console.log(`[OrganizationManageDialog] ✅ Loaded ${membersData.length} members and ${teamsData.length} teams`);
      setMembers(membersData);
      setTeams(teamsData);
    } catch (error) {
      console.error("Error loading organization data:", error);
      toast({
        title: "Error",
        description: "Failed to load organization data",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadData();
    }
  }, [open, organization.id]);

  const handleEdit = async (values: EditFormValues) => {
    try {
      await updateOrganization(
        organization.id,
        values.name,
        values.description
      );
      toast({
        title: "Success",
        description: "Organization updated successfully",
      });
      setEditMode(false);
      onSuccess?.();
    } catch (error) {
      console.error("Error updating organization:", error);
      toast({
        title: "Error",
        description: "Failed to update organization",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async () => {
    try {
      await deleteOrganization(organization.id);
      toast({
        title: "Success",
        description: "Organization deleted successfully",
      });
      setOpen(false);
      onDelete?.();
    } catch (error) {
      console.error("Error deleting organization:", error);
      toast({
        title: "Error",
        description: "Failed to delete organization",
        variant: "destructive",
      });
    }
  };

  const handleRemoveMember = async (member: OrganizationMember) => {
    try {
      await removeOrganizationMember(organization.id, member.user_id);
      toast({
        title: "Success",
        description: "Member removed successfully",
      });
      loadData();
    } catch (error) {
      console.error("Error removing member:", error);
      toast({
        title: "Error",
        description: "Failed to remove member",
        variant: "destructive",
      });
    }
  };

  // Invite functionality handlers
  const handleAddInvite = async () => {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!inviteEmail.trim()) {
      setInviteErrors({ submit: 'Please enter an email address' });
      return;
    }

    if (!emailPattern.test(inviteEmail)) {
      setInviteErrors({ submit: 'Please enter a valid email address' });
      return;
    }

    if (pendingInvites.includes(inviteEmail)) {
      setInviteErrors({ submit: 'This email is already in the list' });
      return;
    }

    // For personal workspaces: Check if user already exists (personal invites are for new users only)
    // For organisations: Skip check (existing users can be invited to join organisations)
    if (isPersonalWorkspace) {
      try {
        const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:3001";
        const token = (await supabase.auth.getSession()).data.session?.access_token;

        const response = await fetch(`${apiUrl}/api/invites/check-email/${encodeURIComponent(inviteEmail)}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          if (data.exists) {
            setInviteErrors({ submit: 'This user already exist' });
            return;
          }
        }
      } catch (error) {
        console.error('Error checking email:', error);
        // Continue anyway - validation will happen on backend
      }
    }

    setPendingInvites(prev => [...prev, inviteEmail]);
    setInviteEmail('');
    setInviteErrors({});
  };

  const handleRemoveInvite = (emailToRemove: string) => {
    setPendingInvites(prev => prev.filter(email => email !== emailToRemove));
  };

  const handleSendInvites = async () => {
    if (pendingInvites.length === 0) {
      setInviteErrors({ submit: 'Please add at least one email address' });
      return;
    }

    setSendingInvites(true);
    setInviteErrors({});

    try {
      const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:3001";
      const token = (await supabase.auth.getSession()).data.session?.access_token;

      // Send invites using the /api/invites/generate endpoint
      for (const email of pendingInvites) {
        // Determine invite type and tenant_id based on workspace type
        const invitePayload = isPersonalWorkspace
          ? {
              receiver_email: email,
              invite_type: 'personal',
              tenant_id: null,
            }
          : {
              receiver_email: email,
              invite_type: 'organisation',
              tenant_id: organization.id,
            };

        const response = await fetch(`${apiUrl}/api/invites/generate`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify(invitePayload),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to send invite');
        }
      }

      // Success toast
      const inviteMessage = isPersonalWorkspace
        ? `Invitation emails sent to ${pendingInvites.length} user(s).`
        : `Invitation emails sent to ${pendingInvites.length} member(s).`;

      toast({
        title: 'Invitations Sent',
        description: inviteMessage,
      });

      // Clear state and close dialog
      setPendingInvites([]);
      setInviteEmail('');
      setInviteDialogOpen(false);

      // Reload data to show new members (if they accept)
      loadData();
    } catch (error) {
      console.error('Error sending invites:', error);
      setInviteErrors({
        submit: error instanceof Error ? error.message : 'Failed to send invitations. Please try again.'
      });
      toast({
        title: 'Error',
        description: 'Failed to send some invitations. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSendingInvites(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          {trigger || (
            <Button variant="outline" size="sm">
              <Settings className="w-4 h-4 mr-2" />
              Manage
            </Button>
          )}
        </DialogTrigger>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <Building className="w-5 h-5" />
              <span>Manage {organization.name}</span>
            </DialogTitle>
          </DialogHeader>

          <Tabs
            value={activeTab}
            onValueChange={setActiveTab}
            className="flex-1"
          >
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="members">
                Members ({members.length})
              </TabsTrigger>
              {/* Teams tab - Commented out for now, will be used in future */}
              {/* <TabsTrigger value="teams">Teams ({teams.length})</TabsTrigger> */}
            </TabsList>

            <div className="overflow-y-auto max-h-[60vh] mt-4">
              <TabsContent value="overview" className="space-y-4">
                {editMode ? (
                  <Form {...editForm}>
                    <form
                      onSubmit={editForm.handleSubmit(handleEdit)}
                      className="space-y-4"
                    >
                      <FormField
                        control={editForm.control}
                        name="name"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Organization Name</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={editForm.control}
                        name="description"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Description</FormLabel>
                            <FormControl>
                              <Textarea {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <div className="flex space-x-2">
                        <Button type="submit">Save Changes</Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setEditMode(false)}
                        >
                          Cancel
                        </Button>
                      </div>
                    </form>
                  </Form>
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold">
                        {isPersonalWorkspace ? 'Workspace Details' : 'Organization Details'}
                      </h3>
                      <div className="flex space-x-2">
                        {/* Invite Button - only for organisation workspaces */}
                        {!isPersonalWorkspace && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setInviteDialogOpen(true)}
                          >
                            <UserPlus className="w-4 h-4 mr-2" />
                            {inviteButtonText}
                          </Button>
                        )}

                        {/* Edit Button */}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setEditMode(true)}
                        >
                          <Edit className="w-4 h-4 mr-2" />
                          Edit
                        </Button>

                        {/* Delete Button */}
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => setDeleteDialogOpen(true)}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </Button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <p>
                        <strong>Name:</strong> {organization.name}
                      </p>
                      <p>
                        <strong>Description:</strong>{" "}
                        {organization.description || "No description"}
                      </p>
                      <p>
                        <strong>Created:</strong>{" "}
                        {new Date(organization.created_at).toLocaleDateString()}
                      </p>
                      <p>
                        <strong>Members:</strong> {members.length}
                      </p>
                      <p>
                        <strong>Teams:</strong> {teams.length}
                      </p>
                    </div>

                  </div>
                )}
              </TabsContent>

              <TabsContent value="members" className="space-y-4">
                {/* <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold">Members</h3>
                  <InviteDialog
                    type="organization"
                    entityId={organization.id}
                    entityName={organization.name}
                    onSuccess={loadData}
                    trigger={
                      <Button size="sm">
                        <UserPlus className="w-4 h-4 mr-2" />
                        Invite Member
                      </Button>
                    }
                  />
                </div> */}

                <div className="space-y-2">
                  {members.map((member) => (
                    <div
                      key={member.id}
                      className="flex items-center justify-between p-3 border rounded-lg"
                    >
                      <div className="flex items-center space-x-3">
                        <Avatar>
                          <AvatarFallback>
                            {member.full_name?.charAt(0) || "U"}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium">
                            {member.full_name || "Unknown"}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {member.email}
                          </p>
                        </div>
                        <Badge
                          variant={
                            member.role === "admin" ? "default" : "secondary"
                          }
                        >
                          {member.role === "admin" ? (
                            <>
                              <Crown className="w-3 h-3 mr-1" />
                              Admin
                            </>
                          ) : (
                            <>
                              <User className="w-3 h-3 mr-1" />
                              Member
                            </>
                          )}
                        </Badge>
                      </div>

                      {member.role !== "admin" && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent>
                            <DropdownMenuItem
                              onClick={() => setMemberToRemove(member)}
                              className="text-destructive"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Remove
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  ))}
                </div>
              </TabsContent>

              {/* Teams tab content - Commented out for now, will be used in future */}
              {/*
              <TabsContent value="teams" className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold">Teams</h3>
                </div>

                <div className="space-y-2">
                  {teams.map((team) => (
                    <div
                      key={team.id}
                      className="flex items-center justify-between p-3 border rounded-lg"
                    >
                      <div>
                        <p className="font-medium">{team.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {team.description || "No description"}
                        </p>
                      </div>
                    </div>
                  ))}

                  {teams.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No teams in this organization yet.</p>
                    </div>
                  )}
                </div>
              </TabsContent>
              */}
            </div>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Delete Organization Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Organization</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{organization.name}"? This action
              cannot be undone and will remove all teams and data associated
              with this organization.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete Organization
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Remove Member Confirmation */}
      <AlertDialog
        open={!!memberToRemove}
        onOpenChange={() => setMemberToRemove(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Member</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove {memberToRemove?.full_name} from
              this organization?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (memberToRemove) {
                  handleRemoveMember(memberToRemove);
                  setMemberToRemove(null);
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remove Member
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Invite Dialog */}
      <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{inviteDialogTitle}</DialogTitle>
            <p className="text-sm text-muted-foreground">
              Enter email addresses to send invitations
            </p>
          </DialogHeader>

          <div className="space-y-4">
            {/* Email Input */}
            <div className="flex gap-2">
              <Input
                type="email"
                placeholder="Enter email address"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddInvite();
                  }
                }}
                disabled={sendingInvites}
              />
              <Button
                type="button"
                variant="outline"
                onClick={handleAddInvite}
                disabled={sendingInvites}
              >
                <Plus className="w-4 h-4 mr-1" />
                Add
              </Button>
            </div>

            {/* Pending Invites List */}
            {pendingInvites.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium">
                  Pending Invites ({pendingInvites.length})
                </p>
                <div className="space-y-2 max-h-[200px] overflow-y-auto">
                  {pendingInvites.map((email) => (
                    <div
                      key={email}
                      className="flex items-center justify-between p-2 bg-muted rounded-md"
                    >
                      <div className="flex items-center space-x-2">
                        <Mail className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm">{email}</span>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveInvite(email)}
                        disabled={sendingInvites}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Error Messages */}
            {inviteErrors.submit && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-700">{inviteErrors.submit}</p>
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-2 mt-4">
            <Button
              variant="outline"
              onClick={() => {
                setInviteDialogOpen(false);
                setPendingInvites([]);
                setInviteEmail('');
                setInviteErrors({});
              }}
              disabled={sendingInvites}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSendInvites}
              disabled={pendingInvites.length === 0 || sendingInvites}
            >
              {sendingInvites ? (
                <>
                  <span className="mr-2">Sending...</span>
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  Send Invites
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

    </>
  );
}
